import { useEffect } from 'react';
import { throttle } from 'lodash';

import { isNil } from '../utils';

import { headerHeight } from '../../components/header/header.module.scss';

// Sticky elements will float this many pixels below the top of the window
const PAGE_HEADER_HEIGHT = parseInt(headerHeight, 10);

/**
 * Hook for creating 'sticky' elements that a fixed in place when the page is scrolled.
 * Usage: pass in the sticky (target) element's ref and the parent container's ref. The
 * sticky elemnt will be constrained to stay within the parent element.
 * You can optionally provide a custom `setY` function to control how the sticky
 * element will be positioned but make sure to pass any recalculated Y value through
 * `clampFn` to make sure the sticky element does not break out of its container element.
 */
export const useStickyElement = (
  targetElement: HTMLElement | null,
  containerElement: HTMLElement | null,
  classNameWhenFloating?: string,
  setY = (y: number, targetElement: HTMLElement, clampFn: (y: number) => number) =>
    (targetElement.style.transform = `translate(0, ${clampFn(y)}px)`),
) => {
  useEffect(() => {
    if (isNil(targetElement) || isNil(containerElement)) {
      return;
    }

    const handleScroll = throttle(() => {
      const { height: targetHeight } = targetElement.getBoundingClientRect();
      const { top, height: containerHeight } = containerElement.getBoundingClientRect();
      const containerStyle = getComputedStyle(containerElement);
      const containerPadding =
        parseInt(containerStyle.paddingTop, 10) + parseInt(containerStyle.paddingBottom, 10);
      const clampY = (y: number) =>
        Math.min(containerHeight - containerPadding - targetHeight, Math.max(0, y));
      const y = clampY(-top + PAGE_HEADER_HEIGHT);
      setY(y, targetElement, clampY);
      if (classNameWhenFloating !== undefined) {
        targetElement.classList.toggle(classNameWhenFloating as string, y > 0);
      }
    }, 16);

    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    return () => {
      document.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [targetElement, containerElement, setY, classNameWhenFloating]);
};
